export const KUDU_SYNC_CHANNEL = 'kudu-sync';
export const KUDU_AUTH_TOKEN_REQUEST = 'KUDU_AUTH_TOKEN_REQUEST';
export const KUDU_AUTH_TOKEN_RESPONSE = 'KUDU_AUTH_TOKEN_RESPONSE';
export const KUDU_REPLAY_QUEUE = 'KUDU_REPLAY_QUEUE';

export type SyncState = 'queued-offline' | 'syncing' | 'synced' | 'failed';

// Why the server refused a queued answer. A report with none is an entry dropped for
// another cause, or one from a worker older than this.
export type SyncFailureReason = 'refused' | 'section-closed';

export interface SyncStatusMessage {
  message?: string;
  reason?: SyncFailureReason;
  requestId: string;
  state: SyncState;
  type: 'sync-status';
}

// Every report the worker sends reaches a page by BroadcastChannel, where the browser
// has one, and as a window event from the bridge (registerServiceWorker): most pages
// hear each one twice. For pages only: a worker has no window to listen on.
export function listenToSyncReports(
  onReport: (report: Partial<SyncStatusMessage>) => void,
) {
  function handleWindowEvent(event: Event) {
    onReport((event as CustomEvent<Partial<SyncStatusMessage>>).detail);
  }

  window.addEventListener(KUDU_SYNC_CHANNEL, handleWindowEvent);

  let channel: BroadcastChannel | null = null;

  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(KUDU_SYNC_CHANNEL);
    channel.onmessage = (event: MessageEvent<Partial<SyncStatusMessage>>) => {
      onReport(event.data);
    };
  }

  return () => {
    window.removeEventListener(KUDU_SYNC_CHANNEL, handleWindowEvent);
    channel?.close();
  };
}

export interface AuthTokenRequestMessage {
  requestId: string;
  type: typeof KUDU_AUTH_TOKEN_REQUEST;
}

export type AuthTokenResponseMessage =
  | {
      requestId: string;
      token: string;
      type: typeof KUDU_AUTH_TOKEN_RESPONSE;
    }
  | {
      reason: 'signed-out' | 'token-error';
      requestId: string;
      type: typeof KUDU_AUTH_TOKEN_RESPONSE;
    };

export interface ReplayQueueMessage {
  type: typeof KUDU_REPLAY_QUEUE;
}

export function createRequestId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `kudu-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

